import { FC, useEffect, useMemo, useState } from 'react'
import { Select } from 'antd'
import type { FormInstance } from 'antd/es/form/Form'
import type { SelectProps } from 'antd'
import FormItem from 'antd/es/form/FormItem'
import { CheckBoxLabel } from '@UI/CheckBox'
import TooltipWrapper from '@UI/New/TooltipWrapper/TooltipWrapper'
import InfoComp from '@pages/Admin/CRMSettings/components/InfoComp/InfoComp'
import { RequestCreateLeadPaymentLink } from '@/api/types/crm/crm.types'
import spclasses from '../../../SetCourseClassDirectionModal/purchase.module.css'
import classes from './index.module.css'
import { CREATE_LINK_BLOCK_TARIFF_TYPES } from '../../tariffTypes'
import { withCreateLinkBlockTariffTypesHint } from '../../createLinkBlockInfoContent'

type FieldType = Omit<RequestCreateLeadPaymentLink, 'lead_funnel_id'>

export type CourseLinkGroupOption = {
  value: number
  label: string
  disabled?: boolean
  disabledReason?: string
  /** Для гидрации group_per_lesson */
  lessonDuration?: number | null
  studentLessonPrice?: number | null
}

type CourseLinkGroupSelectProps = {
  form: FormInstance
  options: CourseLinkGroupOption[]
  loading?: boolean
  /** Сброс чекбокса при смене курса */
  courseId?: number | null
}

const NO_GROUPS_TOOLTIP =
  'В выбранном курсе пока нет групп / потоков. Создайте группу в настройках курса, чтобы распределить ученика.'

export const SHARED_BALANCE_GROUP_DISABLED_TOOLTIP =
  'Нельзя выбрать группу с общим балансом для группового тарифа с оплатой по занятию'

type FilterOption = SelectProps['filterOption']

const filterOptionsByLabel: FilterOption = (input, option) =>
  ((option?.labelString as string) ?? '').toLowerCase().includes(input.toLowerCase())

/**
 * Чекбокс «Распределить ученика в группу» + селект группы
 * для CRM-ссылки (free / month / stream_one_time / stream_custom_period / group_per_lesson).
 */
const CourseLinkGroupSelect: FC<CourseLinkGroupSelectProps> = ({
  form,
  options,
  loading = false,
  courseId,
}) => {
  const [isAssignToGroup, setIsAssignToGroup] = useState(false)
  const hasGroups = options.length > 0
  const isCheckboxDisabled = !loading && !hasGroups

  const selectOptions = useMemo(
    () =>
      options.map((option) => {
        const isDisabled = Boolean(option.disabled)
        const labelText = option.label

        return {
          value: option.value,
          disabled: isDisabled,
          labelString: labelText,
          label: isDisabled ? (
            <TooltipWrapper
              placement="top"
              title={option.disabledReason ?? SHARED_BALANCE_GROUP_DISABLED_TOOLTIP}
            >
              {/* pointerEvents: antd disabled option режет hover — без этого тултип не всплывёт */}
              <span className={classes.disabledOptionLabel}>{labelText}</span>
            </TooltipWrapper>
          ) : (
            labelText
          ),
        }
      }),
    [options],
  )

  useEffect(() => {
    setIsAssignToGroup(false)
    form.setFieldValue('group_id', null)
  }, [courseId, form])

  useEffect(() => {
    if (isCheckboxDisabled && isAssignToGroup) {
      setIsAssignToGroup(false)
      form.setFieldValue('group_id', null)
    }
  }, [form, isAssignToGroup, isCheckboxDisabled])

  useEffect(() => {
    const selectedId = form.getFieldValue('group_id') as number | null
    if (selectedId == null) return

    const selected = options.find((item) => item.value === Number(selectedId))
    if (!selected || selected.disabled) {
      form.setFieldValue('group_id', null)
    }
  }, [form, options])

  const onToggleAssign = (checked?: boolean) => {
    if (isCheckboxDisabled) return

    const nextChecked =
      typeof checked === 'boolean' ? checked : !isAssignToGroup
    setIsAssignToGroup(nextChecked)
    if (!nextChecked) {
      form.setFieldValue('group_id', null)
    }
  }

  const checkbox = (
    <CheckBoxLabel
      type="button"
      value={isAssignToGroup}
      disabled={isCheckboxDisabled}
      onClick={onToggleAssign}
      label="Распределить ученика в группу"
      className={spclasses.checkboxWithLabel}
      style={{
        justifyContent: 'flex-start',
        paddingTop: '4px',
        paddingBottom: '4px',
        height: 'auto',
      }}
    />
  )

  return (
    <div className={spclasses.inputWithLabel}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {isCheckboxDisabled ? (
          <TooltipWrapper placement="top" title={NO_GROUPS_TOOLTIP}>
            <span className={classes.disabledCheckboxWrap}>{checkbox}</span>
          </TooltipWrapper>
        ) : (
          checkbox
        )}
        <InfoComp
          text={withCreateLinkBlockTariffTypesHint(
            'Можно заранее выбрать группу / поток, в которую ученик будет распределён после оплаты по ссылке.',
            CREATE_LINK_BLOCK_TARIFF_TYPES.groupSelect,
          )}
        />
      </div>

      {isAssignToGroup && !isCheckboxDisabled ? (
        <div style={{ paddingTop: '4px' }}>
          <div className={spclasses.inputLabel}>Группа / поток</div>
          <FormItem<FieldType>
            name="group_id"
            rules={[
              {
                required: true,
                message: 'Выберите группу или снимите чекбокс',
              },
            ]}
            noStyle
          >
            <Select
              showSearch
              loading={loading}
              placeholder="Выберите группу / поток"
              className={spclasses.select}
              popupClassName={classes.selectPopup}
              filterOption={filterOptionsByLabel}
              options={selectOptions}
            />
          </FormItem>
        </div>
      ) : (
        <FormItem<FieldType> name="group_id" hidden>
          <input type="hidden" />
        </FormItem>
      )}
    </div>
  )
}

export default CourseLinkGroupSelect
